import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Actions from 'actions/Actions';
import {
    colors, fontSizes, fontWeights, space
} from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import LocaleUtils from 'utils/LanguageLocale';
import { Box, Icon, Text } from 'components/ui';

/**
 * AcceleratedPromoInfo displays promotional information for auto-replenishment subscriptions
 * with accelerated discount offers (Hot Deals).
 *
 * @param {Object} props - Component props
 * @param {Object} props.localization - Localized text strings for the component
 * @param {Object} props.hotDealData - Promotional discount data from useSubscriptionItem hook
 * @returns {React.ReactElement} Promotional information UI with optional info modal trigger
 */
function AcceleratedPromoInfo({ localization, hotDealData }) {
    const dispatch = useDispatch();

    /**
     * Opens an informational modal displaying the full terms and conditions
     * of the Auto-Replenish Hot Deal promotion.
     */
    const openHotDealModal = useCallback(() => {
        dispatch(
            Actions.showInfoModal({
                isOpen: true,
                bodyFooterPaddingX: 4,
                bodyFooterPaddingY: 4,
                showCancelButtonLeft: false,
                title: localization.autoReplenishPromoInfoModalTitle,
                message: (
                    <Text is='p'>
                        {localization.autoReplenishPromoInfoModalMsg1}
                        <br />
                        <br />
                        {localization.autoReplenishPromoInfoModalMsg2}
                    </Text>
                ),
                buttonText: localization.autoReplenishPromoInfoModalButton
            })
        );
    }, [dispatch, localization]);

    return hotDealData.acceleratedDiscountReclaimPossible ? (
        <Text
            is='p'
            css={[styles.container, styles.reclaimPossible]}
        >
            {localization.or}, <Text css={styles.savePercentage}>{hotDealData.savePercentageText}</Text> {localization.getItBefore}{' '}
            <Text is='strong'>{hotDealData.formattedPromoExpiryDate}</Text>
        </Text>
    ) : (
        <Box css={styles.container}>
            <Text
                is='p'
                data-at={Sephora.debug.dataAt('sku_accelerated_promotion_terms')}
                css={[styles.legalCopy, styles.text]}
            >
                *<Text is='strong'>{hotDealData.remainingOrderCount}</Text>
                {` ${hotDealData.discountDeliveriesLeft}`}
            </Text>
            <Box
                css={[
                    styles.text,
                    !LocaleUtils.isFrench() && {
                        whiteSpace: 'nowrap'
                    }
                ]}
            >
                {hotDealData.discountsValidUntilMessage}
                <Icon
                    size={16}
                    marginLeft={1}
                    name='infoOutline'
                    cursor='pointer'
                    data-at={Sephora.debug.dataAt('hot_deal_modal_trigger')}
                    onClick={openHotDealModal}
                />
            </Box>
        </Box>
    );
}

const styles = {
    container: {
        marginBottom: space[4]
    },
    reclaimPossible: {
        fontSize: fontSizes.xs,
        whiteSpace: 'nowrap'
    },
    legalCopy: {
        whiteSpace: 'nowrap',
        display: 'block'
    },
    savePercentage: {
        fontWeight: fontWeights.bold,
        color: colors.red,
        whiteSpace: 'nowrap'
    },
    text: {
        fontSize: fontSizes.sm,
        color: colors.gray
    }
};

AcceleratedPromoInfo.propTypes = {
    localization: PropTypes.shape({
        or: PropTypes.string,
        getItBefore: PropTypes.string,
        autoReplenishPromoInfoModalTitle: PropTypes.string.isRequired,
        autoReplenishPromoInfoModalMsg1: PropTypes.string.isRequired,
        autoReplenishPromoInfoModalMsg2: PropTypes.string.isRequired,
        autoReplenishPromoInfoModalButton: PropTypes.string.isRequired
    }).isRequired,
    hotDealData: PropTypes.shape({
        acceleratedDiscountReclaimPossible: PropTypes.bool,
        savePercentageText: PropTypes.string,
        formattedPromoExpiryDate: PropTypes.string,
        remainingOrderCount: PropTypes.number,
        discountDeliveriesLeft: PropTypes.string,
        discountsValidUntilMessage: PropTypes.string
    }).isRequired
};

export default wrapFunctionalComponent(AcceleratedPromoInfo, 'AcceleratedPromoInfo');
